import { useState, useMemo, useEffect, lazy, Suspense } from 'react';
import { Compass, Bookmark, X } from 'lucide-react';

import Layout from './components/Layout';
import OurStory from './components/OurStory';
import ContactUs from './components/ContactUs';
import Carousel from './components/Carousel';
import HeroSection from './components/HeroSection';
import PackageGrid from './components/PackageGrid';
import ExpeditionCard from './components/ExpeditionCard';
import Testimonials from './components/Testimonials';
import GroupTripSection from './components/GroupTripSection';
import type { LegalDocType } from './components/LegalModal';

const ItineraryDrawer = lazy(() => import('./components/ItineraryDrawer'));
const QuoteWizard = lazy(() => import('./components/QuoteWizard'));
const BookingModal = lazy(() => import('./components/BookingModal'));
const LegalModal = lazy(() => import('./components/LegalModal'));

import { EXPEDITIONS } from './data';
import type { Expedition } from './types';
import { useTranslation } from './hooks/useTranslation';
import { useAnalytics } from './hooks/useAnalytics';


import BabyLion from './assets/BabyLion.webp';
import GroupTripMongoose from './assets/GroupTripMongoose.webp';

interface HistoryNavigationState {
	tab: string;
	drawerId: string | null;
	bookingId: string | null;
	inquiriesOpen: boolean;
	legalDoc?: LegalDocType | null;
}

const parseNavigationHash = (hashStr: string): HistoryNavigationState => {
	const clean = (hashStr || '').replace(/^#\/?/, '').trim();
	if (!clean || clean === 'discover' || clean === 'home') {
		return { tab: 'discover', drawerId: null, bookingId: null, inquiriesOpen: false, legalDoc: null };
	}
	if (clean === 'terms' || clean === 'terms-and-conditions') {
		return { tab: 'discover', drawerId: null, bookingId: null, inquiriesOpen: false, legalDoc: 'terms' };
	}
	if (clean === 'privacy' || clean === 'privacy-policy') {
		return { tab: 'discover', drawerId: null, bookingId: null, inquiriesOpen: false, legalDoc: 'privacy' };
	}
	if (clean === 'indemnity' || clean === 'liability') {
		return { tab: 'discover', drawerId: null, bookingId: null, inquiriesOpen: false, legalDoc: 'indemnity' };
	}
	if (clean.startsWith('expedition=')) {
		const id = clean.replace('expedition=', '');
		return { tab: 'expeditions', drawerId: id, bookingId: null, inquiriesOpen: false, legalDoc: null };
	}
	if (clean.startsWith('book=')) {
		const id = clean.replace('book=', '');
		return { tab: 'expeditions', drawerId: null, bookingId: id, inquiriesOpen: false, legalDoc: null };
	}
	if (clean === 'inquiries') {
		return { tab: 'discover', drawerId: null, bookingId: null, inquiriesOpen: true, legalDoc: null };
	}
	if (clean === 'expeditions' || clean === 'safaris') {
		return { tab: 'expeditions', drawerId: null, bookingId: null, inquiriesOpen: false, legalDoc: null };
	}
	if (clean === 'story' || clean === 'about') {
		return { tab: 'story', drawerId: null, bookingId: null, inquiriesOpen: false, legalDoc: null };
	}
	if (clean === 'builder' || clean === 'planner' || clean === 'bespoke' || clean === 'quote') {
		return { tab: 'builder', drawerId: null, bookingId: null, inquiriesOpen: false, legalDoc: null };
	}
	if (clean === 'contact') {
		return { tab: 'contact', drawerId: null, bookingId: null, inquiriesOpen: false, legalDoc: null };
	}
	if (clean === 'saved' || clean === 'bookmarks') {
		return { tab: 'saved', drawerId: null, bookingId: null, inquiriesOpen: false, legalDoc: null };
	}
	return { tab: 'discover', drawerId: null, bookingId: null, inquiriesOpen: false, legalDoc: null };
};

const getHashFromNav = (nav: {
	tab: string;
	drawerId?: string | null;
	bookingId?: string | null;
	inquiriesOpen?: boolean;
	legalDoc?: LegalDocType | null;
}): string => {
	if (nav.legalDoc) return `#${nav.legalDoc}`;
	if (nav.inquiriesOpen) return '#inquiries';
	if (nav.bookingId) return `#book=${nav.bookingId}`;
	if (nav.drawerId) return `#expedition=${nav.drawerId}`;
	if (nav.tab && nav.tab !== 'discover') return `#${nav.tab}`;
	return '#discover';
};

export default function App() {
	const { t, translateExpeditions } = useTranslation();
	const { trackEvent } = useAnalytics();

	// Translate expeditions dynamically
	const localizedExpeditions = useMemo(() => {
		return translateExpeditions(EXPEDITIONS);
	}, [translateExpeditions]);

	// Initial route state from hash
	const initialNav = useMemo(() => parseNavigationHash(typeof window !== 'undefined' ? window.location.hash : ''), []);

	// Navigation States
	const [activeTab, setActiveTab] = useState<string>(initialNav.tab);

	// Interactive Overlays
	const [activeDrawerExpedition, setActiveDrawerExpedition] = useState<Expedition | null>(null);
	const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
	const [activeBookingExpedition, setActiveBookingExpedition] = useState<Expedition | null>(null);
	const [isBookingOpen, setIsBookingOpen] = useState<boolean>(false);
	const [selectedQuotePackageId, setSelectedQuotePackageId] = useState<string | null>(null);
	const [selectedTravelStyle, setSelectedTravelStyle] = useState<string | null>(null);

	// Saved bookmark list
	const [savedIds, setSavedIds] = useState<string[]>([]);

	// Bookings logged history
	const [bookings, setBookings] = useState<any[]>([]);
	const [showInquiriesModal, setShowInquiriesModal] = useState<boolean>(false);
	const [showcaseImgLoaded, setShowcaseImgLoaded] = useState<boolean>(false);

	// Legal Documents Modal State
	const [isLegalModalOpen, setIsLegalModalOpen] = useState<boolean>(initialNav.legalDoc !== null);
	const [activeLegalDoc, setActiveLegalDoc] = useState<LegalDocType>(initialNav.legalDoc || 'terms');

	const handleOpenLegal = (doc: LegalDocType) => {
		setActiveLegalDoc(doc);
		setIsLegalModalOpen(true);
		try {
			window.history.pushState(
				{ tab: activeTab, drawerId: null, bookingId: null, inquiriesOpen: false, legalDoc: doc },
				'',
				`#${doc}`,
			);
		} catch (e) {
			console.warn('History pushState error', e);
		}
	};

	const handleCloseLegal = () => {
		setIsLegalModalOpen(false);
		try {
			const hash = activeTab && activeTab !== 'discover' ? `#${activeTab}` : '';
			window.history.pushState(
				{ tab: activeTab, drawerId: null, bookingId: null, inquiriesOpen: false, legalDoc: null },
				'',
				hash || window.location.pathname + window.location.search,
			);
		} catch (e) {
			console.warn('History pushState error', e);
		}
	};

	// Load from localStorage if available (fail-safe for SPA lifecycle)
	useEffect(() => {
		try {
			const cachedSaved = localStorage.getItem('ee_saved_ids');
			if (cachedSaved) setSavedIds(JSON.parse(cachedSaved));

			const cachedBookings = localStorage.getItem('ee_bookings_history');
			if (cachedBookings) setBookings(JSON.parse(cachedBookings));
		} catch (e) {
			console.warn('localStorage integration omitted in sandboxed frame.', e);
		}
	}, []);

	// Robust scroll to top helper
	const forceScrollToTop = () => {
		try {
			window.scrollTo(0, 0);
			document.documentElement.scrollTop = 0;
			document.body.scrollTop = 0;

			// Delay to handle React DOM updates
			setTimeout(() => {
				window.scrollTo({ top: 0, behavior: 'instant' as any });
				document.documentElement.scrollTop = 0;
				document.body.scrollTop = 0;
			}, 30);

			setTimeout(() => {
				window.scrollTo(0, 0);
			}, 100);
		} catch (e) {
			console.warn('Scroll failed:', e);
		}
	};

	// Initialize history and deep links on initial mount
	useEffect(() => {
		const initial = parseNavigationHash(window.location.hash);
		setActiveTab(initial.tab);

		if (initial.legalDoc) {
			setActiveLegalDoc(initial.legalDoc);
			setIsLegalModalOpen(true);
		} else if (initial.drawerId) {
			const exp = localizedExpeditions.find(e => e.id === initial.drawerId);
			if (exp) {
				setActiveDrawerExpedition(exp);
				setIsDrawerOpen(true);
			}
		} else if (initial.bookingId) {
			const exp = localizedExpeditions.find(e => e.id === initial.bookingId);
			if (exp) {
				setActiveBookingExpedition(exp);
				setIsBookingOpen(true);
			}
		} else if (initial.inquiriesOpen) {
			setShowInquiriesModal(true);
		}

		const hash = getHashFromNav(initial);
		try {
			window.history.replaceState(
				initial,
				'',
				hash === '#discover' ? window.location.pathname + window.location.search : hash,
			);
		} catch (e) {
			console.warn('History replaceState not permitted in iframe', e);
		}
	}, [localizedExpeditions]);

	// Listen for browser Back/Forward navigation
	useEffect(() => {
		const handlePopState = (event: PopStateEvent) => {
			const target: HistoryNavigationState = event.state || parseNavigationHash(window.location.hash);

			if (target.legalDoc) {
				setActiveLegalDoc(target.legalDoc);
				setIsLegalModalOpen(true);
				setShowInquiriesModal(false);
				setIsDrawerOpen(false);
				setIsBookingOpen(false);
				return;
			}
			setIsLegalModalOpen(false);

			if (target.inquiriesOpen) {
				setShowInquiriesModal(true);
				setIsDrawerOpen(false);
				setIsBookingOpen(false);
				return;
			}
			setShowInquiriesModal(false);

			if (target.bookingId) {
				const exp = localizedExpeditions.find(e => e.id === target.bookingId) || null;
				setActiveBookingExpedition(exp);
				setIsBookingOpen(true);
				setIsDrawerOpen(false);
				if (target.tab) setActiveTab(target.tab);
				return;
			}
			setIsBookingOpen(false);
			setActiveBookingExpedition(null);

			if (target.drawerId) {
				const exp = localizedExpeditions.find(e => e.id === target.drawerId) || null;
				setActiveDrawerExpedition(exp);
				setIsDrawerOpen(true);
				if (target.tab) setActiveTab(target.tab);
				return;
			}
			setIsDrawerOpen(false);
			setActiveDrawerExpedition(null);

			const targetTab = target.tab || 'discover';
			setActiveTab(prev => {
				if (prev !== targetTab) {
					forceScrollToTop();
				}
				return targetTab;
			});
		};

		window.addEventListener('popstate', handlePopState);
		return () => window.removeEventListener('popstate', handlePopState);
	}, [localizedExpeditions]);

	// Synchronized Navigation Actions
	const handleNavigateTab = (newTab: string) => {
		if (activeTab === newTab && !isDrawerOpen && !isBookingOpen && !showInquiriesModal && !isLegalModalOpen) {
			forceScrollToTop();
			return;
		}

		setActiveTab(newTab);
		setIsDrawerOpen(false);
		setActiveDrawerExpedition(null);
		setIsBookingOpen(false);
		setActiveBookingExpedition(null);
		setShowInquiriesModal(false);
		setIsLegalModalOpen(false);

		const navState: HistoryNavigationState = {
			tab: newTab,
			drawerId: null,
			bookingId: null,
			inquiriesOpen: false,
			legalDoc: null,
		};
		const hash = getHashFromNav(navState);
		try {
			window.history.pushState(
				navState,
				'',
				hash === '#discover' ? window.location.pathname + window.location.search : hash,
			);
		} catch (e) {
			console.warn('History pushState error', e);
		}
		forceScrollToTop();
	};

	const handleOpenDrawer = (expedition: Expedition) => {
		setActiveDrawerExpedition(expedition);
		setIsDrawerOpen(true);
		setIsBookingOpen(false);
		setShowInquiriesModal(false);

		trackEvent('select_item', {
			item_id: expedition.id,
			item_name: expedition.title,
			category: 'expedition',
		});

		const navState: HistoryNavigationState = {
			tab: activeTab,
			drawerId: expedition.id,
			bookingId: null,
			inquiriesOpen: false,
		};
		try {
			window.history.pushState(navState, '', `#expedition=${expedition.id}`);
		} catch (e) {
			console.warn('History pushState error', e);
		}
	};

	const handleCloseDrawer = () => {
		if (isDrawerOpen) {
			if (window.location.hash.startsWith('#expedition=')) {
				window.history.back();
			} else {
				setIsDrawerOpen(false);
				setActiveDrawerExpedition(null);
				try {
					const hash = getHashFromNav({ tab: activeTab });
					window.history.replaceState(
						{ tab: activeTab, drawerId: null, bookingId: null, inquiriesOpen: false },
						'',
						hash === '#discover' ? window.location.pathname + window.location.search : hash,
					);
				} catch (e) {
					console.warn(e);
				}
			}
		}
	};

	const handleInquirePackage = (packageId: string) => {
		setSelectedQuotePackageId(packageId);
		setIsDrawerOpen(false);
		setActiveDrawerExpedition(null);
		setIsBookingOpen(false);
		setActiveBookingExpedition(null);
		setShowInquiriesModal(false);
		handleNavigateTab('builder');
	};

	const handleCloseBooking = () => {
		if (isBookingOpen) {
			if (window.location.hash.startsWith('#book=')) {
				window.history.back();
			} else {
				setIsBookingOpen(false);
				setActiveBookingExpedition(null);
				try {
					const hash = getHashFromNav({ tab: activeTab });
					window.history.replaceState(
						{ tab: activeTab, drawerId: null, bookingId: null, inquiriesOpen: false },
						'',
						hash === '#discover' ? window.location.pathname + window.location.search : hash,
					);
				} catch (e) {
					console.warn(e);
				}
			}
		}
	};

	const handleOpenInquiries = () => {
		setShowInquiriesModal(true);
		setIsDrawerOpen(false);
		setIsBookingOpen(false);

		const navState: HistoryNavigationState = {
			tab: activeTab,
			drawerId: null,
			bookingId: null,
			inquiriesOpen: true,
		};
		try {
			window.history.pushState(navState, '', '#inquiries');
		} catch (e) {
			console.warn('History pushState error', e);
		}
	};

	const handleCloseInquiries = () => {
		if (showInquiriesModal) {
			if (window.location.hash === '#inquiries') {
				window.history.back();
			} else {
				setShowInquiriesModal(false);
				try {
					const hash = getHashFromNav({ tab: activeTab });
					window.history.replaceState(
						{ tab: activeTab, drawerId: null, bookingId: null, inquiriesOpen: false },
						'',
						hash === '#discover' ? window.location.pathname + window.location.search : hash,
					);
				} catch (e) {
					console.warn(e);
				}
			}
		}
	};

	// Sync saved list
	const toggleSave = (id: string) => {
		const isSaved = savedIds.includes(id);
		let updated;
		if (isSaved) {
			updated = savedIds.filter(savedId => savedId !== id);
			trackEvent('remove_from_wishlist', { item_id: id });
		} else {
			updated = [...savedIds, id];
			trackEvent('add_to_wishlist', { item_id: id });
		}
		setSavedIds(updated);
		try {
			localStorage.setItem('ee_saved_ids', JSON.stringify(updated));
		} catch (e) {
			console.error('Failed to save saved IDs:', e);
		}
	};

	// Add booking trigger
	const handleAddNewBooking = (newBooking: any) => {
		const updated = [newBooking, ...bookings];
		setBookings(updated);
		trackEvent('generate_lead', {
			category: 'booking',
			label: newBooking.title || newBooking.expeditionTitle,
			value: newBooking.price || newBooking.totalAmount,
		});
		try {
			localStorage.setItem('ee_bookings_history', JSON.stringify(updated));
		} catch (e) {
			console.error('Failed to save bookings history:', e);
		}
	};

	// Saved expeditions list
	const savedExpeditions = useMemo(() => {
		return localizedExpeditions.filter(exp => savedIds.includes(exp.id));
	}, [localizedExpeditions, savedIds]);

	// Handle select travel styles from homepage grid
	const handleSelectTravelStyle = (styleId: string) => {
		setSelectedTravelStyle(styleId);
		handleNavigateTab('expeditions');
	};

	return (
		<Layout
			activeTab={activeTab}
			setActiveTab={handleNavigateTab}
			savedCount={savedIds.length}
			onOpenBookingHistory={handleOpenInquiries}
			hasBookings={bookings.length > 0}
			onOpenLegal={handleOpenLegal}
		>
			{activeTab === 'discover' && (
				<div id='discover-view' className='space-y-24 animate-fade-in'>
					{/* Immersive Hero Header block */}
					<HeroSection
						onExploreClick={() => {
							const element = document.getElementById('travel-styles-anchor');
							if (element) {
								element.scrollIntoView({ behavior: 'smooth' });
							}
						}}
						onPlanClick={() => {
							setSelectedQuotePackageId(null);
							handleNavigateTab('builder');
						}}
					/>

					{/* Travel Styles Package Grid */}
					<PackageGrid onSelectStyle={handleSelectTravelStyle} />

					{/* Safari Highlights Visual Carousel */}
					<Carousel />

					{/* Customizer Callout feature strip */}
					<section className='bg-brand-forest text-earth-cream relative py-16 overflow-hidden'>
						<div className='absolute inset-0 z-0 opacity-15'>
							<img
								src={GroupTripMongoose}
								alt='Group of Mongoose in the African wilderness'
								className='w-full h-full object-cover'
								referrerPolicy='no-referrer'
							/>
						</div>

						<div className='relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6'>
							<span className='font-sans text-[10px] tracking-[0.3em] text-brand-gold font-bold uppercase block'>
								{t('tailoredWilderness')}
							</span>
							<h3 className='font-serif text-3xl sm:text-4xl text-white font-medium max-w-xl mx-auto leading-tight'>
								{t('craftBespokeMasterpiece')}
							</h3>
							<p className='font-sans text-xs sm:text-sm text-earth-sand-light max-w-2xl mx-auto font-light leading-relaxed'>
								{t('craftBespokeSubtitle')}
							</p>
							<div className='pt-2'>
								<button
									onClick={() => {
										setSelectedQuotePackageId(null);
										handleNavigateTab('builder');
									}}
									className='bg-brand-gold hover:bg-brand-gold-light text-brand-forest-dark font-sans text-xs tracking-widest uppercase font-bold py-3.5 px-8 rounded-lg cursor-pointer transition-transform active:scale-95 duration-200'
								>
									{t('openBespokePlanner')}
								</button>
							</div>
						</div>
					</section>

					{/* The conservation-driven model showcase */}
					<section className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center py-4'>
						<div className='lg:col-span-5 relative h-96 rounded-2xl overflow-hidden shadow-md bg-earth-sand/20'>
							{/* Skeleton Shimmer */}
							{!showcaseImgLoaded && (
								<div className='absolute inset-0 bg-linear-to-r from-earth-sand/10 via-earth-sand/35 to-earth-sand/10 animate-pulse flex items-center justify-center'>
									<Compass className='w-8 h-8 text-brand-gold/40 animate-spin [animation-duration:15s]' />
								</div>
							)}
							<img
								src={BabyLion}
								alt='Elephant herd in tall yellow grass'
								loading='lazy'
								decoding='async'
								onLoad={() => setShowcaseImgLoaded(true)}
								className={`w-full h-full object-cover transition-all duration-700 ease-in-out ${
									showcaseImgLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
								}`}
								referrerPolicy='no-referrer'
							/>
							<div className='absolute inset-0 bg-linear-to-t from-black/50 via-transparent to-transparent'></div>
							<div className='absolute bottom-6 left-6 text-white font-serif'>
								<span className='text-brand-gold font-sans text-xs font-bold uppercase tracking-widest block mb-1'>
									{t('ourHeritage')}
								</span>
								<span className='text-xl font-bold'>{t('safeguardingIvoryPathfinders')}</span>
							</div>
						</div>

						<div className='lg:col-span-7 space-y-6'>
							<span className='font-sans text-[10px] tracking-[0.25em] text-brand-gold-dark font-extrabold uppercase block'>
								{t('conservationPartnerships')}
							</span>
							<h3 className='font-serif text-2xl sm:text-3.5xl text-brand-forest font-bold leading-tight'>
								{t('safarisThatProtect')}
							</h3>
							<p className='font-sans text-xs sm:text-sm text-brand-charcoal-light leading-relaxed font-light'>
								{t('safarisThatProtectDesc')}
							</p>

							<div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
								<a
									href='https://wildlifeact.com/'
									target='_blank'
									rel='noopener noreferrer'
									className='flex flex-col sm:flex-row items-center sm:items-start space-y-3 sm:space-y-0 sm:space-x-4 bg-white p-4 rounded-xl border border-earth-sand/35 hover:border-brand-gold/60 hover:shadow-md transition-all duration-200 group text-center sm:text-left'
								>
									<div className='h-12 w-28 shrink-0 flex items-center justify-center bg-white rounded-lg p-1'>
										<img
											src='https://cdn.prod.website-files.com/655cbee32f7e5cfd123ab518/655e2445edc9d067f75af642_logo1.avif'
											alt='Wildlife ACT'
											loading='lazy'
											decoding='async'
											className='max-h-full max-w-full object-contain'
											referrerPolicy='no-referrer'
										/>
									</div>
									<div className='flex-1 min-w-0'>
										<p className='font-sans text-[11px] text-brand-charcoal-light font-light mt-0.5'>
											{t('empoweringCommunityRangers')}
										</p>
									</div>
								</a>
								<a
									href='https://zululandconservationtrust.org/'
									target='_blank'
									rel='noopener noreferrer'
									className='flex flex-col sm:flex-row items-center sm:items-start space-y-3 sm:space-y-0 sm:space-x-4 bg-white p-4 rounded-xl border border-earth-sand/35 hover:border-brand-gold/60 hover:shadow-md transition-all duration-200 group text-center sm:text-left'
								>
									<div className='h-12 w-28 shrink-0 flex items-center justify-center bg-white rounded-lg p-1'>
										<img
											src='https://zululandconservationtrust.org/wp-content/uploads/2020/05/logo_hi-res.jpg'
											alt='Zululand Conservation Trust'
											loading='lazy'
											decoding='async'
											className='max-h-full max-w-full object-contain'
											referrerPolicy='no-referrer'
										/>
									</div>
									<div className='flex-1 min-w-0'>
										<p className='font-sans text-[11px] text-brand-charcoal-light font-light mt-0.5'>
											{t('strictDoubleOccupancy')}
										</p>
									</div>
								</a>
							</div>
						</div>
					</section>

					{/* Group Trip Showcase Section */}
					<GroupTripSection onInquire={() => handleNavigateTab('contact')} />

					{/* Beautiful Interactive Guest Testimonials Block */}
					<Testimonials />
				</div>
			)}

			{activeTab === 'expeditions' && (
				<div id='expeditions-view' className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 animate-fade-in space-y-10'>
					{/* Header Title and Search Summary */}
					<div className='flex flex-col md:flex-row md:items-end justify-between border-b border-earth-sand/30 pb-6 gap-4'>
						<div>
							<h2 className='font-serif text-3xl text-brand-forest font-bold tracking-tight'>
								{t('curatedSafariExpeditionsTitle')}
							</h2>
						</div>
					</div>

					{/* Main Expeditions Grid */}
					<div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
						{localizedExpeditions.map(exp => (
							<ExpeditionCard
								key={exp.id}
								expedition={exp}
								isSaved={savedIds.includes(exp.id)}
								onToggleSave={() => toggleSave(exp.id)}
								onViewItinerary={() => handleOpenDrawer(exp)}
								onBookNow={() => handleInquirePackage(exp.id)}
							/>
						))}
					</div>
				</div>
			)}

			{activeTab === 'builder' && (
				<div className='pt-4 animate-fade-in'>
					{/* Header / Intro section for Wizard */}
					<div className='text-center max-w-2xl mx-auto mb-8 px-4'>
						<h2 className='font-serif text-3xl sm:text-4xl text-brand-forest font-bold tracking-tight'>
							{t('designYourWildernessJourney')}
						</h2>
						<p className='font-sans text-xs sm:text-sm text-brand-charcoal-light leading-relaxed font-light mt-2'>
							{t('designYourWildernessJourneyDesc')}
						</p>
					</div>

					<Suspense fallback={null}>
						<QuoteWizard
							initialPackageId={selectedQuotePackageId}
							initialTravelStyle={selectedTravelStyle}
							onAddBespokeBooking={handleAddNewBooking}
						/>
					</Suspense>
				</div>
			)}

			{/* Saved/Favorited page */}
			{activeTab === 'saved' && (
				<div id='saved-view' className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 animate-fade-in space-y-10'>
					<div className='border-b border-earth-sand/30 pb-6'>
						<span className='font-sans text-[10px] tracking-[0.25em] text-brand-gold-dark font-extrabold uppercase block mb-1'>
							Curated custom wishlists
						</span>
						<h2 className='font-serif text-3xl text-brand-forest font-bold tracking-tight'>My Saved Safaris</h2>
						<p className='font-sans text-xs text-brand-charcoal-light font-light mt-1'>
							Review your prioritized adventures or trigger booking consultations directly.
						</p>
					</div>

					{savedExpeditions.length > 0 ? (
						<div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
							{savedExpeditions.map(exp => (
								<ExpeditionCard
									key={exp.id}
									expedition={exp}
									isSaved={true}
									onToggleSave={() => toggleSave(exp.id)}
									onViewItinerary={() => handleOpenDrawer(exp)}
									onBookNow={() => handleInquirePackage(exp.id)}
								/>
							))}
						</div>
					) : (
						<div className='text-center py-20 bg-white rounded-xl border border-earth-sand/20 space-y-4 max-w-md mx-auto'>
							<Bookmark className='w-12 h-12 text-brand-gold mx-auto' />
							<h3 className='font-serif text-lg text-brand-forest font-bold'>No Safaris Cached</h3>
							<p className='font-sans text-xs text-brand-charcoal-light font-light leading-relaxed'>
								Click the heart icon on any curated safari card while browsing to cache your priorities here.
							</p>
							<div className='pt-2'>
								<button
									onClick={() => handleNavigateTab('expeditions')}
									className='bg-brand-forest text-white font-sans text-xs py-2.5 px-6 rounded-lg uppercase tracking-widest font-semibold cursor-pointer'
								>
									Go to Curated Safaris
								</button>
							</div>
						</div>
					)}
				</div>
			)}

			{activeTab === 'story' && <OurStory />}

			{activeTab === 'contact' && <ContactUs />}

			{/* CURATED ITINERARY EXPLORATION DRAWER */}
			<Suspense fallback={null}>
				<ItineraryDrawer
					expedition={activeDrawerExpedition}
					isOpen={isDrawerOpen}
					onClose={handleCloseDrawer}
					onBookExpedition={() => {
						if (activeDrawerExpedition) {
							handleInquirePackage(activeDrawerExpedition.id);
						}
					}}
				/>
			</Suspense>

			{/* CURATED BOOKING CHECKOUT MODAL */}
			<Suspense fallback={null}>
				<BookingModal
					expedition={activeBookingExpedition}
					isOpen={isBookingOpen}
					onClose={handleCloseBooking}
					onAddBooking={handleAddNewBooking}
				/>
			</Suspense>

			{/* MY SUBMITTED INQUIRIES MODAL DRAWER OVERLAY */}
			{showInquiriesModal && (
				<div
					id='booking-history-backdrop'
					className='fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4'
				>
					<div onClick={handleCloseInquiries} className='fixed inset-0 bg-brand-forest-dark/65 backdrop-blur-xs'></div>
					<div className='relative bg-earth-cream w-full max-w-lg rounded-2xl shadow-xl p-6 z-10 space-y-4'>
						<div className='flex justify-between items-center border-b border-earth-sand/30 pb-3'>
							<h3 className='font-serif text-lg text-brand-forest font-bold'>My Active Inquiries</h3>
							<button
								onClick={handleCloseInquiries}
								className='text-brand-charcoal-light hover:text-brand-forest p-1 rounded-full cursor-pointer'
								title='Close inquiries modal'
							>
								<X className='w-5 h-5' />
							</button>
						</div>

						<div className='space-y-3.5 max-h-[60vh] overflow-y-auto pr-1'>
							{bookings.map(booking => (
								<div
									key={booking.id}
									className='bg-white border border-earth-sand/20 rounded-xl p-4 space-y-2.5 shadow-xs text-left'
								>
									<div className='flex justify-between items-baseline mb-1'>
										<span className='font-mono text-[10.5px] text-brand-terracotta font-bold'>{booking.id}</span>
										<span className='text-[10px] text-brand-charcoal-light font-light font-sans'>
											Logged {booking.date}
										</span>
									</div>

									{booking.type === 'curated' ? (
										<div>
											<h4 className='font-serif text-sm font-bold text-brand-forest leading-tight'>
												{booking.expeditionTitle}
											</h4>
											<div className='grid grid-cols-2 gap-2 pt-2 text-[11px] font-sans text-brand-charcoal-light'>
												<div>
													<strong>Guests:</strong> {booking.travelers} PAX
												</div>
												<div>
													<strong>Date:</strong> {booking.travelDate}
												</div>
												<div>
													<strong>Commute:</strong> {booking.flightTier}
												</div>
												<div>
													<strong>Culinary:</strong> {booking.dietary}
												</div>
											</div>
										</div>
									) : (
										<div>
											<h4 className='font-serif text-sm font-bold text-brand-forest leading-tight'>
												Bespoke {booking.durationDays}-Day Journey
											</h4>
											<div className='text-[11px] font-sans text-brand-charcoal-light pt-2 space-y-1'>
												<div>
													<strong>Destinations:</strong> {booking.locations.join(' → ')}
												</div>
												<div className='grid grid-cols-2 gap-1'>
													<div>
														<strong>Guests:</strong> {booking.travelers} PAX
													</div>
													<div>
														<strong>Tier Level:</strong> {booking.tier}
													</div>
												</div>
											</div>
										</div>
									)}

									<div className='flex justify-between items-baseline border-t border-earth-sand/15 pt-2 mt-2'>
										<div className='flex items-center space-x-1 font-sans text-[10px] text-green-700 font-bold uppercase'>
											<span className='w-1.5 h-1.5 rounded-full bg-green-500'></span>
											<span>Review active</span>
										</div>
										<span className='font-sans text-[10.5px] text-brand-gold-dark font-semibold uppercase tracking-wider'>
											Pending Specialist Review
										</span>
									</div>
								</div>
							))}
						</div>

						<div className='pt-2'>
							<button
								onClick={handleCloseInquiries}
								className='w-full bg-brand-forest hover:bg-brand-forest-light text-earth-cream font-sans text-xs uppercase tracking-widest font-semibold py-3 rounded-lg cursor-pointer'
							>
								Close Inquiry Panel
							</button>
						</div>
					</div>
				</div>
			)}

			{/* LEGAL & COMPLIANCE MODAL */}
			<Suspense fallback={null}>
				<LegalModal isOpen={isLegalModalOpen} initialDoc={activeLegalDoc} onClose={handleCloseLegal} />
			</Suspense>

		</Layout>
	);
}
